import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getAppointments, getProfessionals } from '../services/api';
import AppointmentActionsModal from '../components/AppointmentActionsModal';
import './AgendaPage.css';

// --- date helpers (local-time, no UTC drift) ------------------------------
function toISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function todayISO() {
  return toISODate(new Date());
}

function shiftISO(dateStr, deltaDays) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + deltaDays);
  return toISODate(dt);
}

// Citame appointment statuses (schema NER-1396): pending | confirmed |
// cancelled | completed | no_show. Each maps to an i18n label + a CSS modifier
// class whose colors come from theme variables (never hardcoded).
const STATUS_CLASS = {
  pending: 'agenda-status--pending',
  confirmed: 'agenda-status--confirmed',
  cancelled: 'agenda-status--cancelled',
  completed: 'agenda-status--completed',
  no_show: 'agenda-status--no-show',
};

function AgendaPage() {
  const { t, i18n } = useTranslation();
  const [date, setDate] = useState(todayISO());
  const [professionals, setProfessionals] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionTarget, setActionTarget] = useState(null);

  const load = useCallback(
    async (forDate) => {
      setLoading(true);
      setError('');
      try {
        const [apptRes, profRes] = await Promise.all([
          getAppointments(forDate),
          getProfessionals(),
        ]);
        setAppointments(apptRes?.appointments || []);
        setProfessionals(profRes?.professionals || []);
      } catch (err) {
        console.error('Error loading agenda:', err);
        setAppointments([]);
        setProfessionals([]);
        setError(t('agenda.loadError'));
      } finally {
        setLoading(false);
      }
    },
    [t]
  );

  useEffect(() => {
    load(date);
  }, [date, load]);

  // Build one column per professional, plus an "unassigned" bucket for any
  // appointment whose professionalId doesn't match a known professional.
  const columns = useMemo(() => {
    const byProf = new Map();
    professionals.forEach((p) => {
      const id = p.professionalId || p.id;
      byProf.set(id, {
        id,
        name: p.name || p.fullName || t('agenda.unnamedProfessional'),
        items: [],
      });
    });
    const unassigned = { id: '__unassigned__', name: t('agenda.unassigned'), items: [] };

    appointments.forEach((a) => {
      const profId = a.professionalId;
      const col = byProf.get(profId) || unassigned;
      col.items.push(a);
    });

    const cols = [...byProf.values()];
    if (unassigned.items.length > 0) cols.push(unassigned);
    // sort each column's appointments by time ("HH:mm")
    cols.forEach((c) => c.items.sort((x, y) => (x.time || '').localeCompare(y.time || '')));
    return cols;
  }, [professionals, appointments, t]);

  const prettyDate = useMemo(() => {
    const [y, m, d] = date.split('-').map(Number);
    try {
      return new Date(y, m - 1, d).toLocaleDateString(i18n.language, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    } catch {
      return date;
    }
  }, [date, i18n.language]);

  const totalCount = appointments.length;

  return (
    <div className="agenda-page">
      <div className="page-header">
        <h1>{t('agenda.title')}</h1>
      </div>

      <div className="agenda-toolbar">
        <div className="agenda-date-nav">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setDate((d) => shiftISO(d, -1))}
            aria-label={t('agenda.prevDay')}
          >
            <span className="material-icons">chevron_left</span>
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => setDate(todayISO())}>
            {t('agenda.today')}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setDate((d) => shiftISO(d, 1))}
            aria-label={t('agenda.nextDay')}
          >
            <span className="material-icons">chevron_right</span>
          </button>
          <input
            type="date"
            className="agenda-date-input"
            value={date}
            onChange={(e) => e.target.value && setDate(e.target.value)}
            aria-label={t('agenda.pickDate')}
          />
        </div>
        <div className="agenda-summary">
          <span className="agenda-pretty-date">{prettyDate}</span>
          {!loading && !error && (
            <span className="agenda-count">
              {t('agenda.appointmentCount', { count: totalCount })}
            </span>
          )}
        </div>
      </div>

      <p className="agenda-readonly-note">{t('agenda.manageHint')}</p>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading">{t('common.loading')}</div>
      ) : professionals.length === 0 && totalCount === 0 ? (
        <div className="empty-state">{t('agenda.empty')}</div>
      ) : (
        <div className="agenda-board">
          {columns.map((col) => (
            <section key={col.id} className="agenda-column">
              <header className="agenda-column-header">
                <span className="agenda-column-name">{col.name}</span>
                <span className="agenda-column-count">{col.items.length}</span>
              </header>
              {col.items.length === 0 ? (
                <div className="agenda-column-empty">{t('agenda.noAppointments')}</div>
              ) : (
                <ul className="agenda-slot-list">
                  {col.items.map((a) => (
                    <li
                      key={a.appointmentId || `${a.time}-${a.clientPhone}`}
                      className="agenda-slot"
                    >
                      <button
                        type="button"
                        className="agenda-slot-btn"
                        onClick={() => setActionTarget(a)}
                        aria-label={t('agenda.manageAppointment')}
                      >
                        <div className="agenda-slot-top">
                          <span className="agenda-slot-time">{a.time || '--:--'}</span>
                          <span className={`agenda-status ${STATUS_CLASS[a.status] || ''}`}>
                            {t(`agenda.status.${a.status}`, a.status || '')}
                          </span>
                        </div>
                        <div className="agenda-slot-client">
                          {a.clientName || a.clientPhone || t('agenda.unknownClient')}
                        </div>
                        {(a.serviceName || a.durationMin) && (
                          <div className="agenda-slot-service">
                            {a.serviceName}
                            {a.durationMin ? ` · ${a.durationMin} ${t('agenda.minutesShort')}` : ''}
                          </div>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>
      )}

      {actionTarget && (
        <AppointmentActionsModal
          appointment={actionTarget}
          professionals={professionals}
          onClose={(changed) => {
            setActionTarget(null);
            if (changed) load(date);
          }}
        />
      )}
    </div>
  );
}

export default AgendaPage;
